import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import { generateProductUrl } from '../../services/urlService';
import { getStockQuantity } from '../../services/collectionService';
import QuickOrderForm from './QuickOrderForm';
import { useCart } from '../../context/CartContext';

function DynamicShopDetailsArea({ product, collection }) {
  const { addToCart } = useCart();
  const [nav1, setNav1] = useState(null);
  const [nav2, setNav2] = useState(null);
  const [val, setVal] = useState(1);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [isAdded, setIsAdded] = useState(false);

  // Helper function to determine stock status and get appropriate CSS class
  const getStockStatus = () => {
    if (!collection || !product) {
      return { text: 'Rupture de stock', className: 'out-of-stock', quantity: 0 };
    }

    // For non-color-indexed products, calculate total stock from all stockCombinations
    if (!collection.isColorIndexed) {
      const totalStock = collection.stockCombinations?.reduce((total, comb) => {
        return total + (comb.quantity || 0);
      }, 0) || 0;

      if (totalStock === 0) {
        return { text: 'Rupture de stock', className: 'out-of-stock', quantity: 0 };
      } else if (totalStock <= 5) {
        return { text: `Stock faible`, className: 'low-stock', quantity: totalStock };
      } else {
        return { text: `En stock`, className: 'in-stock', quantity: totalStock };
      }
    }

    // For color-indexed products, get stock based on current selection
    const currentColorName = selectedColor ? selectedColor.label : null;
    const currentSize = selectedSize;
    const currentFeatures = selectedFeatures;

    // Get total stock quantity for this product
    const totalStock = getStockQuantity(collection, currentColorName, currentSize, currentFeatures);

    if (totalStock === 0) {
      return { text: 'Rupture de stock', className: 'out-of-stock', quantity: 0 };
    } else if (totalStock <= 5) { // Low stock threshold
      return { text: `Stock faible`, className: 'low-stock', quantity: totalStock };
    } else {
      return { text: `En stock`, className: 'in-stock', quantity: totalStock };
    }
  };

  // Helper function to determine the pivot type
  // Priority: colors > sizes > first feature
  const getPivotType = () => {
    if (product?.colors && product.colors.length > 0 && product.colors.some(c => c.id !== 'default')) {
      return 'color';
    }
    if (collection?.sizes && collection.sizes.length > 0) {
      return 'size';
    }
    if (collection?.features && collection.features.length > 0) {
      return 'feature';
    }
    return null;
  };

  // Helper function to check if the current selection is out of stock
  const isCurrentSelectionOutOfStock = () => {
    if (!collection || !collection.stockCombinations) {
      return true;
    }

    // Find matching stock combinations based on current selection
    const foundMatch = collection.stockCombinations.some(combo => {
      // Check color match - only if we have colors AND a color is selected
      const hasColors = product?.colors && product.colors.length > 0 && product.colors.some(c => c.id !== 'default');
      if (hasColors && selectedColor && combo.color !== selectedColor.label) {
        return false;
      }

      // Check size match - only if we have sizes AND a size is selected
      const hasSizes = collection?.sizes && collection.sizes.length > 0;
      if (hasSizes && selectedSize && combo.size !== selectedSize) {
        return false;
      }

      // Check features match
      if (selectedFeatures.length > 0) {
        const allFeaturesMatch = selectedFeatures.every(sf =>
          combo.features && combo.features.some(cf =>
            cf.name === sf.name && cf.value === sf.value
          )
        );
        if (!allFeaturesMatch) return false;
      }

      return combo.quantity > 0;
    });

    return !foundMatch;
  };

  // Helper function to check if a color is out of stock (has 0 combinations)
  const isColorOutOfStock = (color) => {
    if (!collection || !collection.stockCombinations) {
      return true;
    }

    // Check if there is ANY combination with this color that has stock
    // We don't filter by other selected attributes because this is the pivot
    // If NO combination exists with this color and quantity > 0, then it's out of stock
    const hasStock = collection.stockCombinations.some(combo => {
      if (combo.color !== color.label) return false;
      return combo.quantity > 0;
    });

    return !hasStock;
  };

  // Helper function to check if a size is out of stock based on the pivot (selected color)
  const isSizeOutOfStock = (size) => {
    if (!collection || !collection.stockCombinations) {
      return true;
    }

    const pivotType = getPivotType();

    // If size is the pivot, check if it has ANY combinations with stock
    if (pivotType === 'size') {
      const hasStock = collection.stockCombinations.some(combo => {
        if (combo.size !== size) return false;
        return combo.quantity > 0;
      });
      return !hasStock;
    }

    // Check stock combinations that match the selected color (if any)
    return !collection.stockCombinations.some(combo => {
      // Must match the size we're checking
      if (combo.size !== size) return false;

      // If colors are the pivot and one is selected, filter by that color
      if (pivotType === 'color' && selectedColor) {
        if (combo.color !== selectedColor.label) return false;
      }

      return combo.quantity > 0;
    });
  };

  // Helper function to check if a feature value is out of stock based on the pivot
  const isFeatureValueOutOfStock = (featureName, featureValue) => {
    if (!collection || !collection.stockCombinations) {
      return true;
    }

    const pivotType = getPivotType();

    // If this feature is the pivot (first feature when no colors/sizes), check if it has ANY combinations with stock
    if (pivotType === 'feature' && collection.features[0].name === featureName) {
      const hasStock = collection.stockCombinations.some(combo => {
        if (!combo.features) return false;
        const hasFeature = combo.features.some(f => f.name === featureName && f.value === featureValue);
        if (!hasFeature) return false;
        return combo.quantity > 0;
      });
      return !hasStock;
    }

    // Check stock combinations
    return !collection.stockCombinations.some(combo => {
      if (!combo.features || combo.quantity <= 0) return false;

      // Must have the feature value we're checking
      const hasFeature = combo.features.some(f => f.name === featureName && f.value === featureValue);
      if (!hasFeature) return false;

      // If colors are the pivot and one is selected, filter by that color
      if (pivotType === 'color' && selectedColor) {
        if (combo.color !== selectedColor.label) return false;
      }

      // If sizes are the pivot and one is selected, filter by that size
      if (pivotType === 'size' && selectedSize) {
        if (combo.size !== selectedSize) return false;
      }

      return true;
    });
  };

  // Helper function to check if a complete combination is selected
  const isSelectionComplete = () => {
    // Check if colors are required and selected
    const hasColors = product?.colors && product.colors.length > 0 && product.colors.some(c => c.id !== 'default');
    if (hasColors && !selectedColor) return false;

    // Check if sizes are required and selected
    const hasSizes = collection?.sizes && collection.sizes.length > 0;
    if (hasSizes && !selectedSize) return false;

    // Check if all features are selected
    const hasFeatures = collection?.features && collection.features.length > 0;
    if (hasFeatures) {
      const allFeaturesSelected = collection.features.every(feature =>
        selectedFeatures.some(sf => sf.name === feature.name)
      );
      if (!allFeaturesSelected) return false;
    }

    return true;
  };

  // Handler for selecting a feature value (toggle behavior)
  const handleFeatureSelect = (featureName, featureValue) => {
    const currentValue = getSelectedFeatureValue(featureName);
    if (currentValue === featureValue) {
      // Unselect if clicking the same value
      setSelectedFeatures(selectedFeatures.filter(f => f.name !== featureName));
    } else {
      // Select new value (replace existing value for this feature)
      const otherFeatures = selectedFeatures.filter(f => f.name !== featureName);
      setSelectedFeatures([...otherFeatures, { name: featureName, value: featureValue }]);
    }
  };

  // Get the currently selected value for a feature
  const getSelectedFeatureValue = (featureName) => {
    const feature = selectedFeatures.find(f => f.name === featureName);
    return feature ? feature.value : null;
  };

  // Handler for selecting a size (toggle behavior)
  const handleSizeSelect = (e, size) => {
    e.preventDefault();
    if (selectedSize === size) {
      // Unselect if clicking the same size
      setSelectedSize(null);
    } else {
      // Select new size
      setSelectedSize(size);
    }
  };

  // Helper function to ensure image URLs have the proper base URL
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:1706';
  const getFullImageUrl = (path) => {
    if (!path) return '';
    // If already has protocol, return as-is
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    // Add leading slash if missing
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${API_URL}${cleanPath}`;
  };

  // Handler for selecting a color (toggle behavior)
  const handleColorSelect = (e, color) => {
    e.preventDefault();
    if (selectedColor && selectedColor.id === color.id) {
      // Unselect if clicking the same color - keep current image
      setSelectedColor(null);
      // Don't change the image - keep the last shown image
    } else {
      // Select new color
      setSelectedColor(color);
      setSelectedImage(getFullImageUrl(color.imagePath || product.img));
    }
  };

  // Initialize with product data
  useEffect(() => {
    if (product) {
      // Set initial color and image
      // Only auto-select a color if it's NOT the default color (products with no colors use 'default')
      if (product.colors && product.colors.length > 0 && product.colors[0].id !== 'default') {
        setSelectedColor(product.colors[0]);
        setSelectedImage(getFullImageUrl(product.colors[0].imagePath || product.img));
      } else {
        // For products with no colors, don't select any color
        setSelectedColor(null);
        setSelectedImage(getFullImageUrl(product.img));
      }
    }
  }, [product]);

  const increase = () => {
    if (isSelectionComplete() && !isCurrentSelectionOutOfStock()) {
      const stockStatus = getStockStatus();
      const availableStock = stockStatus.quantity;
      // Don't exceed available stock
      if (val < availableStock) {
        setVal(val + 1);
      }
    }
  };

  const decrease = () => {
    if (val > 1 && isSelectionComplete() && !isCurrentSelectionOutOfStock()) {
      setVal(val - 1);
    }
  };

  const settings = {
    dots: false,
    infinite: true,
    speed: 1000,
    autoplay: false,
    arrows: false,
    autoplaySpeed: 3000,
    slidesToShow: 4,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
          infinite: true,
        }
      },
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          speed: 1000,
        }
      },
      {
        breakpoint: 767,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          arrows: false,
          speed: 1000,
        }
      },
      {
        breakpoint: 575,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          speed: 1000,
        }
      },
    ]
  };

  // Helper function to normalize image paths for comparison
  const normalizeImagePath = (path) => {
    if (!path) return '';

    // Remove protocol and domain/host:port for comparison
    let normalized = path.replace(/^https?:\/\/[^\/]+\//, '');
    // Remove leading slashes
    normalized = normalized.replace(/^\//, '');
    return normalized.toLowerCase();
  };

  // Get all product images for the slider
  const getAllProductImages = () => {
    if (!product) return [];

    const images = [];
    const seenImages = new Set();
    const seenFullUrls = new Set();

    // Helper to add image if not already seen
    const addImage = (imgPath) => {
      if (!imgPath) return;

      const fullUrl = getFullImageUrl(imgPath);

      // Check exact full URL match first
      if (seenFullUrls.has(fullUrl)) return;

      // Then check normalized path match
      const normalizedPath = normalizeImagePath(imgPath);
      if (!seenImages.has(normalizedPath)) {
        images.push(fullUrl);
        seenImages.add(normalizedPath);
        seenFullUrls.add(fullUrl);
      }
    };

    // Start with main product image
    addImage(product.img);

    // Add color images if available
    if (product.colors && product.colors.length > 0) {
      product.colors.forEach(color => {
        addImage(color.imagePath);
      });
    }

    // Add additional images if available, but filter out description images
    if (product.images && product.images.length > 0) {
      product.images.forEach(img => {
        if (img && !img.includes('_desc')) {
          addImage(img);
        }
      });
    }

    return images.filter(img => img); // Remove empty entries
  };

  // Check if an image corresponds to an out-of-stock color
  const getImageStockStatus = (imgUrl) => {
    if (!product || !product.colors) return false;
    const fullImgUrl = getFullImageUrl(imgUrl);

    // Find color associated with this image
    // Note: getFullImageUrl is needed for comparison if color.imagePath is relative
    const color = product.colors.find(c => getFullImageUrl(c.imagePath) === fullImgUrl);

    if (color) {
      return isColorOutOfStock(color);
    }
    return false;
  };

  const productImages = getAllProductImages();
  const isOutOfStock = isCurrentSelectionOutOfStock();
  const isIncomplete = !isSelectionComplete();
  const isButtonDisabled = isIncomplete || isOutOfStock;

  // Check if current selected image is OOS
  const isSelectedImageOOS = getImageStockStatus(selectedImage);

  console.log('Product Images:', productImages);
  return (
    <section className="shop-details-area pt-110 pb-50">
      <div className="container">
        <div className="shop-details-container">
          <div className="shop-details-wrap">
            <div className="row">
              <div className="col-lg-7 col-md-6">
                <div className="shop-details-img-wrap">
                  <div className="tab-content" id="myTabContent">
                    <div className="tab-pane show active" id="item-one" role="tabpanel" aria-labelledby="item-one-tab">
                      <div className="shop-details-img" style={{ position: 'relative' }}>
                        <img src={selectedImage || 'img/product/shop_details01.jpg'} alt={product.name || 'Product'} style={{ width: "100%", height: "auto", border: '1px solid #ddd', opacity: isSelectedImageOOS ? 0.6 : 1 }} />
                        {isSelectedImageOOS && (
                          <>
                            <div style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              backgroundColor: 'rgba(255, 255, 255, 0.2)',
                              zIndex: 3,
                              borderRadius: '4px'
                            }} />
                            <div style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              color: '#dc3545',
                              padding: '12px 24px',
                              borderRadius: '6px',
                              fontSize: '24px',
                              fontWeight: '700',
                              textTransform: 'uppercase',
                              letterSpacing: '1px',
                              zIndex: 4,
                              textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
                              whiteSpace: 'nowrap',
                              backgroundColor: 'rgba(255, 255, 255, 0.8)'
                            }}>
                              Rupture de Stock
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {productImages.length > 1 && (
                  <div className="shop-details-nav-wrap">
                    <ul className="nav nav-tabs" id="myTab" role="tablist">
                      {productImages.map((img, index) => {
                        const isImgOOS = getImageStockStatus(img);
                        return (
                          <li className="nav-item" role="presentation" key={index}>
                            <a
                              className={`nav-link ${index === 0 ? 'active' : ''}`}
                              id={`item-${index + 1}-tab`}
                              data-toggle="tab"
                              href={`/#item-${index + 1}`}
                              role="tab"
                              aria-controls={`item-${index + 1}`}
                              aria-selected={index === 0}
                              onClick={(e) => {
                                e.preventDefault();
                                setSelectedImage(img);
                                document.querySelectorAll('.shop-details-nav-wrap .nav-link').forEach(el => {
                                  el.classList.remove('active');
                                });
                                e.target.classList.add('active');
                              }}
                              style={{ position: 'relative', display: 'block' }}
                            >
                              <img
                                src={img}
                                alt={`Thumbnail ${index + 1}`}
                                style={{
                                  width: '121px',
                                  height: '123px',
                                  objectFit: 'cover',
                                  border: '1px solid #ddd',
                                  opacity: isImgOOS ? 0.6 : 1
                                }}
                              />
                              {isImgOOS && (
                                <div style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: 'rgba(255,255,255,0.4)',
                                  zIndex: 2
                                }}>
                                  <span style={{
                                    color: '#dc3545',
                                    fontSize: '10px',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    backgroundColor: 'rgba(255,255,255,0.9)',
                                    padding: '2px 4px',
                                    borderRadius: '4px',
                                    border: '1px solid #dc3545'
                                  }}>
                                    RUPTURE
                                  </span>
                                </div>
                              )}
                            </a>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
              <div className="col-lg-5 col-md-6">
                <div className="shop-details-content">
                  <h2 className="title">{product.name}</h2>
                  {/* <div className="shop-details-review">
                  <div className="rating">
                    <i className="fas fa-star" />
                    <i className="fas fa-star" />
                    <i className="fas fa-star" />
                    <i className="fas fa-star" />
                    <i className="fas fa-star" />
                  </div>
                  <span>( 01 Review )</span>
                </div> */}
                  <div className="shop-details-price">
                    <h2 className="price">{product.price || '$52.00'}</h2>
                    {collection && (
                      <h5 className={`stock-status ${getStockStatus().className}`}>- {getStockStatus().text}</h5>
                    )}
                  </div>
                  <p>{product.description || product.shortDescription || 'The domestic dog is a doiated dendant of the wolf. The dog derived from an ancient, extinct wolf, and the modern grey wolf is the dog\'s nearest living relative.'}</p>


                  {product.colors && product.colors.length > 0 && product.colors.some(color => color.id !== 'default') && (
                    <div className="shop-details-color">
                      <span>Couleur :</span>
                      <ul>
                        {product.colors.map((color, index) => {
                          const colorOutOfStock = isColorOutOfStock(color);
                          return (
                            <li
                              key={index}
                              className={`${color.id} ${selectedColor && selectedColor.id === color.id ? 'active' : ''}`}
                              onClick={(e) => {
                                if (!colorOutOfStock) handleColorSelect(e, color)
                              }}
                              style={{
                                backgroundColor: color.hex,
                                position: 'relative',
                                cursor: colorOutOfStock ? 'not-allowed' : 'pointer',
                                opacity: colorOutOfStock ? 0.3 : 1
                              }}
                              title={colorOutOfStock ? `${color.label} (Rupture de stock)` : color.label}
                            >
                              {colorOutOfStock && (
                                <span style={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  width: '140%',
                                  height: '2px',
                                  backgroundColor: '#888',
                                  transform: 'translate(-50%, -50%) rotate(-45deg)',
                                  pointerEvents: 'none'
                                }} />
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {collection?.sizes && collection.sizes.length > 0 && (
                    <div className="shop-details-dimension">
                      <span>Taille :</span>
                      <ul>
                        {collection.sizes.map((size, index) => {
                          const sizeOutOfStock = isSizeOutOfStock(size);
                          return (
                            <li key={index} className={selectedSize === size ? 'active' : ''}>
                              <a
                                href="/#"
                                onClick={(e) => handleSizeSelect(e, size)}
                                style={{
                                  cursor: 'pointer',
                                  position: 'relative',
                                  opacity: sizeOutOfStock ? 0.6 : 1,
                                  overflow: 'hidden'
                                }}
                                title={sizeOutOfStock ? `${size} (Rupture de stock)` : size}
                              >
                                {size}
                                {sizeOutOfStock && (
                                  <span style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    width: '200%',
                                    height: '2px',
                                    backgroundColor: '#888',
                                    transform: 'translate(-50%, -50%) rotate(-45deg)',
                                    pointerEvents: 'none'
                                  }} />
                                )}
                              </a>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}



                  {/* Features from collection - each feature has a name and values array */}
                  {collection?.features && collection.features.length > 0 && collection.features.map((feature, featureIndex) => (
                    <div className="shop-details-dimension" key={featureIndex}>
                      <span>{feature.name} :</span>
                      <ul>
                        {feature.values && feature.values.map((value, valueIndex) => {
                          const isSelected = getSelectedFeatureValue(feature.name) === value;
                          const featureOutOfStock = isFeatureValueOutOfStock(feature.name, value);
                          return (
                            <li
                              key={valueIndex}
                              className={isSelected ? 'active' : ''}
                            >
                              <a
                                href="/#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleFeatureSelect(feature.name, value);
                                }}
                                style={{
                                  cursor: 'pointer',
                                  position: 'relative',
                                  opacity: featureOutOfStock ? 0.6 : 1,
                                  overflow: 'hidden'
                                }}
                                title={featureOutOfStock ? `${value} (Rupture de stock)` : value}
                              >
                                {value}
                                {featureOutOfStock && (
                                  <span style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    width: '200%',
                                    height: '2px',
                                    backgroundColor: '#888',
                                    transform: 'translate(-50%, -50%) rotate(-45deg)',
                                    pointerEvents: 'none'
                                  }} />
                                )}
                              </a>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}

                  <div className="shop-details-quantity">
                    <span>Quantité :</span>
                    <div className={`cart-plus-minus ${isButtonDisabled ? 'disabled' : ''}`}>
                      <input type="text" value={val} readOnly />
                      <div
                        className={`dec qtybutton ${isButtonDisabled ? 'disabled' : ''}`}
                        onClick={decrease}
                        style={{
                          cursor: isButtonDisabled ? 'not-allowed' : 'pointer',
                          opacity: isButtonDisabled ? 0.5 : 1
                        }}
                      >-</div>
                      <div
                        className={`inc qtybutton ${isButtonDisabled || val >= getStockStatus().quantity ? 'disabled' : ''}`}
                        onClick={increase}
                        style={{
                          cursor: (isButtonDisabled || val >= getStockStatus().quantity) ? 'not-allowed' : 'pointer',
                          opacity: (isButtonDisabled || val >= getStockStatus().quantity) ? 0.5 : 1
                        }}
                      >+</div>
                    </div>
                    <a
                      className={`cart-btn ${isButtonDisabled ? 'out-of-stock-btn' : ''}`}
                      onClick={() => {
                        if (isButtonDisabled) return;

                        // Construct options object
                        const options = {
                          collection: collection?.name,
                          color: selectedColor?.label || selectedColor?.id || null,
                          size: selectedSize || null,
                          features: selectedFeatures && selectedFeatures.length > 0 ?
                            selectedFeatures.reduce((acc, curr) => ({ ...acc, [curr.name]: curr.value }), {}) :
                            undefined,
                          images: [selectedColor?.imagePath || product.img]
                        };

                        addToCart(product, val, options);

                        // Show feedback via button state
                        setIsAdded(true);
                        setTimeout(() => setIsAdded(false), 3000);
                      }}
                      style={{
                        cursor: isButtonDisabled ? 'not-allowed' : 'pointer',
                        opacity: isButtonDisabled ? 0.7 : 1,
                        backgroundColor: isButtonDisabled ? '#999' : (isAdded ? '#4BB543' : undefined),
                        color: isAdded ? '#fff' : undefined,
                        transition: 'all 0.3s ease'
                      }}
                    >{isIncomplete ? 'Sélectionnez les options' : (isOutOfStock ? 'Rupture de stock' : (isAdded ? 'Ajouté au panier !' : 'Ajouter au panier +'))}</a>
                  </div>
                  <div className="shop-details-bottom">
                    <ul>
                      {collection && (
                        <li className="sd-category">
                          <span className="title">Collection :</span>
                          <Link to="#">{collection.name}</Link>
                        </li>
                      )}
                      <li className="sd-sku">
                        <span className="title">SKU :</span>
                        <Link to="#">{product.productId || 'H#21546'}</Link>
                      </li>
                      <li className="sd-share">
                        <span className="title">Partager :</span>
                        <a
                          href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Share on Facebook"
                        ><i className="fab fa-facebook-f" /></a>
                        <a
                          href={`https://www.instagram.com/?url=${encodeURIComponent(window.location.href)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Share on Instagram"
                        ><i className="fab fa-instagram" /></a>
                        <a
                          href={`https://www.tiktok.com/share?url=${encodeURIComponent(window.location.href)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Share on TikTok"
                        ><i className="fab fa-tiktok" /></a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <QuickOrderForm
                product={product}
                collection={collection}
                selectedColor={selectedColor}
                selectedSize={selectedSize}
                selectedFeatures={selectedFeatures}
                quantity={val}
                isButtonDisabled={isButtonDisabled}
                isOutOfStock={isOutOfStock}
                onOrderSuccess={(orderData) => {
                  console.log('Order created:', orderData);
                  // Optional: scroll directly to the success message or form could be nice
                  const formElement = document.querySelector('.quick-order-form-container');
                  if (formElement) formElement.scrollIntoView({ behavior: 'smooth' });
                }}
              />
              <div className="product-desc-wrap">
                <ul className="nav nav-tabs" id="myTabTwo" role="tablist">
                  <li className="nav-item">
                    <a className="nav-link active" id="details-tab" data-toggle="tab" href="/#details" role="tab" aria-controls="details" aria-selected="true">Détails</a>
                  </li>
                  <li className="nav-item">
                    <a className="nav-link" id="val-tab" data-toggle="tab" href="/#val" role="tab" aria-controls="val" aria-selected="false">Informations</a>
                  </li>
                  {/* <li className="nav-item">
                    <a className="nav-link" id="review-tab" data-toggle="tab" href="/#review" role="tab" aria-controls="review" aria-selected="false">Reviews (0)</a>
                  </li> */}
                </ul>
                <div className="tab-content" id="myTabContentTwo">
                  <div className="tab-pane fade show active" id="details" role="tabpanel" aria-labelledby="details-tab">
                    <div className="product-desc-content">
                      <p>{product.information || product.description || 'The domestic dog is a doiated dendant of the wolf. The dog derived from an ancient, extinct wolf, and the modern grey wolf is the dog\'s nearest living relative. The dog was the first species to be domesticated, by hunter–gatherers. These will include the core vaccines, which are administered in a series of three: at 6-, 12-, and 16 weeks old.'}</p>
                    </div>
                  </div>
                  <div className="tab-pane fade" id="val" role="tabpanel" aria-labelledby="val-tab">
                    <div className="product-desc-info">
                      <div className="row">
                        <div className="col-xl-3 col-md-5">
                          <div className="product-desc-img">
                            <img src={'img/product/desc_img.jpg'} alt="Product Description" />
                          </div>
                        </div>
                        <div className="col-xl-9 col-md-7">
                          <h5 className="small-title">100% {product.name}</h5>
                          <p>{product.shortDescription || 'Cramond Leopard & Pythong Print Anorak Jacket In Beige but also the leap into electronic typesetting, remaining lorem Ipsum is simply dummy text of the printing and typesetting industry.'}</p>
                          <ul className="product-desc-list">
                            <li>65% poly, 35% rayon</li>
                            <li>Partially lined</li>
                            <li>Hidden front button closure with keyhole accents</li>
                            <li>Button cuff sleeves</li>
                            <li>Lightweight semi-sheer fabrication</li>
                            <li>Made in USA</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="tab-pane fade" id="review" role="tabpanel" aria-labelledby="review-tab">
                    <div className="product-desc-review">
                      <div className="review-title mb-20">
                        <h4 className="title">Customer Reviews (0)</h4>
                      </div>
                      <div className="left-rc">
                        <p>No reviews yet</p>
                      </div>
                      <div className="right-rc">
                        <a href="/#">Write a review</a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default DynamicShopDetailsArea;